import { localStorageKey, MapStore, SelectionIdentifier } from "./store";
import { captureException } from "../../util/monitoring";
import { RouterMapDetailsType } from "./initialiseMap";

const parseUrlParams = (urlParams: RouterMapDetailsType) => {
  const formatted = { zoom: Number(urlParams.z), mapX: Number(urlParams.x), mapY: Number(urlParams.y) };
  if (Object.values(formatted).some((v) => isNaN(v) || v === 0)) {
    return;
  }
  return formatted;
};

export const getUrlParams = ({
  routerDetails,
}: {
  routerDetails?: RouterMapDetailsType;
}): { mapX: number; mapY: number; zoom: number } | undefined => {
  let params: { mapX: number; mapY: number; zoom: number } | undefined;
  const coords = ["x", "y", "z"] as (keyof RouterMapDetailsType)[];
  if (routerDetails && coords.every((k) => routerDetails[k] !== undefined)) {
    params = parseUrlParams(routerDetails);
  }
  if (!params) {
    const coordinatesMatch = window.location.pathname.match(
      /(\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)(?=\/|$)/
    );
    if (coordinatesMatch) {
      const [z, x, y] = coordinatesMatch[0].split(",");
      params = parseUrlParams({ z, x, y });
    }
  }
  return params;
};

/**
 * Parse a comma-separated "z,lat,lng" segment from the URL (e.g. Vue route gives one param).
 */
function parseCommaSeparatedZ(z: string): { zoom?: number; mapX?: number; mapY?: number } | null {
  const parts = z.split(",").map((s) => s.trim());
  if (parts.length !== 3) return null;
  const [zNum, xNum, yNum] = parts.map((p) => (p ? parseFloat(p) : NaN));
  if (isNaN(zNum) || isNaN(xNum) || isNaN(yNum)) return null;
  return { zoom: zNum, mapX: xNum, mapY: yNum };
}

export const getPathParameters = (routerDetails: RouterMapDetailsType) => {
  const { x, y, z, siteId, publicApp } = routerDetails;
  const fromCommaZ = typeof z === "string" && z.includes(",") ? parseCommaSeparatedZ(z) : null;
  return {
    siteId,
    ...(fromCommaZ ?? {}),
    ...(!fromCommaZ && typeof z === "string" && !isNaN(parseInt(z, 10)) && { zoom: parseInt(z, 10) }),
    ...(!fromCommaZ && typeof x === "string" && !isNaN(parseFloat(x)) && { mapX: parseFloat(x) }),
    ...(!fromCommaZ && typeof y === "string" && !isNaN(parseFloat(y)) && { mapY: parseFloat(y) }),
    ...{ publicApp },
  };
};

export const getSettingsFromLocalStorage = (): MapStore["settings"] | undefined => {
  try {
    return JSON.parse(window.localStorage.getItem(localStorageKey) || "{}");
  } catch (err) {
    captureException(err);
    window.localStorage.removeItem(localStorageKey);
    return undefined;
  }
};

export const getLastLogoutReason = () => {
  try {
    const lastLogoutReason = localStorage.getItem("lastLogoutReason");
    localStorage.removeItem("lastLogoutReason");
    return lastLogoutReason;
  } catch (err) {
    captureException(err);
    return undefined;
  }
};

export function getMapCenterClamped(googleMap: google.maps.Map) {
  return new google.maps.LatLng(googleMap.getCenter()?.toJSON() ?? { lat: 53, lng: 0 });
}

export function isMatchingSelection(selectionA: SelectionIdentifier, selectionB: SelectionIdentifier) {
  return selectionA.id === selectionB.id && selectionA.type === selectionB.type;
}
